import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

// Constants for practitioner filters
const FILTER_OPTIONS = {
  STATUS: ['Active', 'Inactive'],
  AVAILABILITY: ['Available Today', 'Not Available']
};

// Reusable class constants
const CLASSES = {
  SECTION: 'flex flex-col gap-3',
  SECTION_TITLE: 'font-poppins font-semibold text-base leading-none text-[#171717] m-0',
  BUTTON_GROUP: 'flex gap-3 items-start flex-wrap',
  SEPARATOR: 'w-full h-px bg-[#E2E8F0]'
};

const FilterButton = ({ children, isSelected, onClick, className = '' }) => {
  const baseClasses = 'h-9 px-[14px] py-1 justify-center items-center gap-1 font-poppins text-[13px] font-semibold leading-6 transition-colors rounded-xl border-0';
  const selectedClasses = isSelected
    ? 'bg-[#2563EB] text-white'
    : 'bg-[#EFF6FF] text-[#334155] hover:bg-[#DBEAFE]';

  return (
    <button
      onClick={onClick}
      aria-pressed={isSelected}
      aria-label={`Filter by ${children}`}
      className={`${baseClasses} ${selectedClasses} ${className}`}
    >
      {children}
    </button>
  );
};

FilterButton.propTypes = {
  children: PropTypes.node.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string
};

function PractitionerFilterSlider({ isOpen, onClose, onApplyFilters, onClearAll }) {
  const [filters, setFilters] = useState({
    status: [],
    availability: []
  });

  const handleFilterToggle = useCallback((category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  }, []);

  const handleClearAll = useCallback(() => {
    setFilters({
      status: [],
      availability: []
    });
    if (onClearAll) onClearAll();
  }, [onClearAll]);

  const handleApply = useCallback(() => {
    if (onApplyFilters) onApplyFilters(filters);
    if (onClose) onClose();
  }, [onApplyFilters, onClose, filters]);

  const handleCancel = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 right-0 h-screen w-[450px] bg-white rounded-l-[24px] z-[1000] shadow-[-4px_0_20px_rgba(0,0,0,0.1)] inline-flex max-md:w-full max-md:rounded-none max-md:left-0 max-md:shadow-none">
      <div className="p-6 h-full flex flex-col gap-6 overflow-y-auto justify-between items-start flex-1 max-md:p-4">
        {/* Header */}
        <div className="flex justify-between items-end w-full max-md:items-center">
          <div className="flex items-center gap-6 max-md:gap-3">
            <button
              onClick={onClose}
              className="bg-none border-none cursor-pointer p-0 w-6 h-6 flex items-center justify-center aspect-square"
              aria-label="Close filter"
            >
              <X className="w-6 h-6 text-[#171717]" />
            </button>
            <h2 className="font-poppins font-bold text-2xl leading-none text-[#171717] m-0 max-md:text-xl">
              Practitioner Filter
            </h2>
          </div>
          <button
            onClick={handleClearAll}
            className="font-poppins font-semibold text-sm leading-none text-[#DC2626] bg-none border-none cursor-pointer p-0 text-center hover:opacity-80"
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        </div>

        <div className={CLASSES.SEPARATOR} />

        <div className="flex-1 flex flex-col gap-6 max-md:gap-4">
          {/* Status */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Status</h3>
            <div className={CLASSES.BUTTON_GROUP}>
              {FILTER_OPTIONS.STATUS.map((status) => (
                <FilterButton
                  key={status}
                  isSelected={filters.status.includes(status)}
                  onClick={() => handleFilterToggle('status', status)}
                >
                  {status}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />

          {/* Availability */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Availability</h3>
            <div className={CLASSES.BUTTON_GROUP}>
              {FILTER_OPTIONS.AVAILABILITY.map((availability) => (
                <FilterButton
                  key={availability}
                  isSelected={filters.availability.includes(availability)}
                  onClick={() => handleFilterToggle('availability', availability)}
                >
                  {availability}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />
        </div>

        {/* Footer */}
        <div className="flex w-full flex-col items-start max-md:gap-3">
          <div className={CLASSES.SEPARATOR} />
          <div className="flex items-start gap-4 pt-4 max-md:gap-3 max-md:flex-col max-md:w-full">
            <button
              onClick={handleCancel}
              className="flex w-[200px] py-3 px-2.5 justify-center items-center gap-2 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-white text-[#334155] font-poppins text-base font-semibold hover:bg-gray-50 transition max-md:w-full"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="flex w-[200px] py-3 px-4 justify-center items-center gap-2 rounded-xl btn-gradient border-0 font-poppins text-base font-semibold hover:opacity-95 transition max-md:w-full"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

PractitionerFilterSlider.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onApplyFilters: PropTypes.func,
  onClearAll: PropTypes.func
};

PractitionerFilterSlider.defaultProps = {
  onClose: null,
  onApplyFilters: null,
  onClearAll: null
};

export default PractitionerFilterSlider;